import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { ViewIcon } from 'lucide-react';

import { AirIdDisplay } from '@/components/air-id-display';
import { ArrowTooltip } from '@/components/arrow-tooltip';
import { CopyButton } from '@/components/copy-button';
import { CopyableLink } from '@/components/copyable-link';
import { LinkToExternal } from '@/components/link-to-external';
import { MeetingStatusBadge } from '@/components/session/meeting-status-badge';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { createTableRowSelectionCell, PinnedComponent } from '@/components/table/entity-table';
import { Button } from '@/components/ui/button';
import { getCoachAttendanceGradeLabel } from '@/lib/coach-utils';
import { getDistrictColumn } from '@/lib/district-utils';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list';
import { SessionTeacherDisplay } from '@/routes/_layout/sessions/-components/session-teacher-display';
import {
  distinct,
  formatDateWithDayOfWeek,
  formatInstant,
  formatTimeRange,
  FrequentTimeZones,
  getSessionSubstitutionRequestSummary,
  isNotNullish,
  safeInstantFromString,
} from '@airreading/common';
import {
  AdminSession,
  OriginalTeacherIssueLabelMap,
  StudentYearColorMap,
  studentYearOption,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { UserPenIcon } from 'lucide-react';
import { z } from 'zod';
import { SubSessionDrawer } from '../sub-sessions/-components/SubSessionDrawer';
import { CheckboxGoogleMeetChecked } from './checkbox-google-meet-checked';
import { CheckboxUpdateSupportAdmin } from './checkbox-update-support-admin';
import { EditSessionModal } from './edit-session-modal';
import { OriginalTeacherDisplay } from './original-teacher-display';
import { SessionCompletionStatusCell } from './session-completion-status-cell';
import { SessionLink } from './session-link';
import { TeacherDisplayTagBadgeV2 } from './teacher-display-tag-badge-v2';

const adminCol = createColumnHelper<AdminSession>();

function CoachRemarksV2Header() {
  return (
    <div className="flex items-center gap-2">
      <span className="font-semibold">Session remarks</span>
    </div>
  );
}

export const sessionPageColumnsEnum = z.enum([
  'sessionId',
  'editSession',
  'date',
  'time',
  'status',
  'meetingStatus',
  'meetingStartInstant',
  'supportAdmin',
  'joinedStudentsCount',
  'sessionTeacher',
  'originalTeacher',
  'teacherAttendanceGrade',
  'videoConferenceType',
  'zoomPersonalMeetingId',
  'zoomLink',
  'zoomHostUrl',
  'googleMeetLink',
  'googleMeetChecked',
  'lessonLink',
  'district',
  'school',
  'grades',
  'groupAttendance',
  'students',
  'numberOfStudents',
  'lessonName',
  'originalTeacherIssue',
  'coachDisplayTags',
  'sessionCompletionStatus',
  'canceledAtInstant',
  'substituteTimestamp',
  'recordingLink',
  'coachSessionRemarksV2',
  'classId',
  'subSessionDrawer',
]);

export const SessionPageColumns = sessionPageColumnsEnum.enum;
export type SessionPageColumns = z.infer<typeof sessionPageColumnsEnum>;

export const sessionsPageColumns = (displayTimeZone: string, from?: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor('id', {
    id: SessionPageColumns.sessionId,
    header: '',
    cell: ({ getValue }) => {
      const searchProps = from ? { search: { from } } : {};
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: getValue() }} {...searchProps}>
          <ViewIcon size={16} />
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  ...(from === 'school'
    ? [
        adminCol.accessor('studentGroupId', {
          id: SessionPageColumns.classId,
          header: 'Class ID',
          cell: ({ getValue }) => {
            const studentGroupId = getValue();
            return (
              <AirIdDisplay
                entityType="cls"
                rawId={studentGroupId}
                wrapper={(displayId) => <span className="text-black">{displayId}</span>}
              />
            );
          },
        }),
      ]
    : []),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue }) => {
      const startInstant = getValue();
      return formatDateWithDayOfWeek(startInstant, displayTimeZone);
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral);
    },
  }),

  adminCol.accessor('meeting.status', {
    id: SessionPageColumns.meetingStatus,
    header: 'Meeting status',
    cell: ({ getValue }) => {
      return <MeetingStatusBadge status={getValue()} />;
    },
  }),

  adminCol.accessor('meeting.startInstant', {
    id: SessionPageColumns.meetingStartInstant,
    header: 'Meeting started timestamp',
    cell: ({ getValue }) => {
      const startedAt = getValue();
      if (!startedAt) {
        return 'Not started';
      }

      const { formattedTime } = formatInstant(startedAt, displayTimeZone, {
        hour12: true,
      });
      return formattedTime.toLowerCase();
    },
  }),

  adminCol.accessor('joinedStudentsCount', {
    id: SessionPageColumns.joinedStudentsCount,
    header: '# of students joined',
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row.students?.length ?? 0, {
    id: SessionPageColumns.numberOfStudents,
    header: '# of students',
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grades',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.org?.name;
      if (!districtName) return <span>Unknown</span>;
      return getDistrictColumn({
        orgId: school.orgId,
        orgName: districtName,
      });
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown</span>;
      return (
        <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
          {school.name}
        </Link>
      );
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!link || !lesson) return '—';

      return (
        <div className="flex items-center gap-2">
          <Link to={'/lessons/$lessonId'} params={{ lessonId: lesson.id }} className="underline">
            {lesson.name}
          </Link>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.teacherAttendanceGrade,
    header: 'Attendance grade - Session teacher',
    cell: ({ getValue }) => {
      const coach = getValue();
      return getCoachAttendanceGradeLabel(coach?.coachProfile?.attendanceGrade);
    },
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.coach?.coachProfile?.zoomPersonalMeetingId, {
    id: SessionPageColumns.zoomPersonalMeetingId,
    header: 'Zoom ID',
    cell: ({ getValue }) => getValue() || '—',
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.zoom?.hostUrl, {
    id: SessionPageColumns.zoomHostUrl,
    header: 'Zoom host URL',
    cell: ({ getValue }) => {
      const link = getValue();
      if (!link) return '—';

      return (
        <div className="flex items-center gap-2">
          <SessionLink href={link}>Open Zoom host link</SessionLink>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor('groupAverageAttendanceLast14Days', {
    id: SessionPageColumns.groupAttendance,
    header: 'Student average attendance percentage - last 14 days',
    cell: ({ getValue }) => {
      const value = getValue();
      return value !== null ? `${value}%` : 'N/A';
    },
  }),

  adminCol.accessor('originalTeacherIssue', {
    id: SessionPageColumns.originalTeacherIssue,
    header: 'Original teacher issue',
    cell: ({ getValue }) => {
      const originalTeacherIssue = getValue();
      if (!originalTeacherIssue) {
        return null;
      }
      return OriginalTeacherIssueLabelMap[originalTeacherIssue];
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionCompletionStatus,
    header: 'Teacher display - Class status',
    cell: ({ getValue }) => {
      const {
        sessionCompletionStatus,
        coachSessionRemarksV2,
        zoom,
        startInstant,
        endInstant,
        addedCoachSessionRemarksV2,
      } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant, addedCoachSessionRemarksV2 }}
        />
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'createdAt',
    header: 'Created time (CT)',
    cell: ({ getValue }) => {
      const { createdAt } = getValue();
      const instant = safeInstantFromString(String(createdAt));
      const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
      return <div>{formattedDateTime}</div>;
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'updatedAt',
    header: 'Last updated time (CT)',
    cell: ({ getValue }) => {
      const { updatedAt } = getValue();
      const instant = safeInstantFromString(String(updatedAt));
      const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
      return <div>{formattedDateTime}</div>;
    },
  }),
];

export const futureSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue }) => {
      const session = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: session.id }} search={{ from: 'future' }}>
          {formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const endInstant = row.original.endInstant;
      const session = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: session.id }} search={{ from: 'future' }}>
          {formatTimeRange(session.startInstant, endInstant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  adminCol.accessor('meeting.status', {
    id: SessionPageColumns.meetingStatus,
    header: 'Meeting status',
    cell: ({ getValue }) => {
      return <MeetingStatusBadge status={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.teacherAttendanceGrade,
    header: 'Teacher attendance',
    cell: ({ getValue }) => {
      const coach = getValue();
      return getCoachAttendanceGradeLabel(coach?.coachProfile?.attendanceGrade);
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school-district',
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      return (school?.name || 'Unknown') + ' - ' + (school?.org?.name || 'Unknown');
    },
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor('canceledAtInstant', {
    id: SessionPageColumns.canceledAtInstant,
    header: 'Cancelation timestamp',
    cell: ({ getValue }) => {
      const canceledAtInstant = getValue();

      if (!canceledAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(canceledAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),
];

export const todaySessionsPageColumns = (displayTimeZone: string, from?: string) => [
  createTableRowSelectionCell(adminCol, { isPinned: true }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue, column }) => (
      <PinnedComponent pinned="left" column={column}>
        <EditSessionModal session={getValue()} />
      </PinnedComponent>
    ),
    size: 60,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, column }) => {
      const searchProps = from ? { search: { from } } : {};
      const session = getValue();
      return (
        <PinnedComponent pinned="left" column={column}>
          <Link to="/sessions/$sessionId" params={{ sessionId: session.id }} {...searchProps}>
            {formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}
          </Link>
        </PinnedComponent>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, column }) => {
      const searchProps = from ? { search: { from } } : {};
      const session = getValue();
      return (
        <PinnedComponent pinned="left" column={column}>
          <Link to="/sessions/$sessionId" params={{ sessionId: session.id }} {...searchProps}>
            {formatTimeRange(session.startInstant, session.endInstant, FrequentTimeZones.UsCentral)}
          </Link>
        </PinnedComponent>
      );
    },
    minSize: 165,
  }),

  adminCol.accessor('meeting.status', {
    id: SessionPageColumns.meetingStatus,
    header: 'Meeting status',
    cell: ({ getValue, column }) => (
      <PinnedComponent pinned="left" column={column}>
        <MeetingStatusBadge status={getValue()} />
      </PinnedComponent>
    ),
    size: 140,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Teacher',
    cell: ({ getValue, column }) => (
      <PinnedComponent pinned="left" column={column}>
        <SessionTeacherDisplay session={getValue()} />
      </PinnedComponent>
    ),
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.supportAdmin,
    header: () => <ArrowTooltip trigger="Support...">Support Admin</ArrowTooltip>,
    cell: ({ getValue }) => {
      const values = getValue();
      return (
        <div className="flex justify-center">
          <CheckboxUpdateSupportAdmin {...values} />
        </div>
      );
    },
    size: 60,
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: () => <ArrowTooltip trigger="Meeting ty...">Meeting type</ArrowTooltip>,
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.zoom?.hostUrl, {
    id: SessionPageColumns.zoomHostUrl,
    header: 'Zoom host URL',
    cell: ({ getValue }) => {
      const link = getValue();
      if (!link) return '—';

      return (
        <div className="flex items-center gap-2">
          <SessionLink href={link}>Open Zoom host link</SessionLink>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row.coach?.coachProfile?.zoomPersonalMeetingId, {
    id: SessionPageColumns.zoomPersonalMeetingId,
    header: 'Zoom ID',
    cell: ({ getValue }) => getValue() || '—',
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.googleMeetChecked,
    header: () => <ArrowTooltip trigger="Google M...">Google Meet checked</ArrowTooltip>,
    cell: ({ getValue }) => {
      const session = getValue();
      return (
        <div className="flex justify-center">
          <CheckboxGoogleMeetChecked {...session} />
        </div>
      );
    },
    size: 60,
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.teacherAttendanceGrade,
    header: () => <ArrowTooltip trigger="Teach...">Teacher attendance</ArrowTooltip>,
    cell: ({ getValue }) => {
      const coach = getValue();
      return getCoachAttendanceGradeLabel(coach?.coachProfile?.attendanceGrade);
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown - Unknown</span>;
      return (
        <>
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name} - <span className="text-blue-600">{school.org.name}</span>
          </Link>
        </>
      );
    },
  }),

  adminCol.accessor('joinedStudentsCount', {
    id: SessionPageColumns.joinedStudentsCount,
    header: () => <ArrowTooltip trigger="#..."># of students joined</ArrowTooltip>,
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} horizontal />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grades',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ getValue }) => {
      const coach = getValue();
      return coach ? <PeopleBadgeList people={[coach]} /> : null;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor('originalTeacherIssue', {
    id: SessionPageColumns.originalTeacherIssue,
    header: () => <ArrowTooltip trigger="Original...">Original teacher issue</ArrowTooltip>,
    cell: ({ getValue }) => {
      const originalTeacherIssue = getValue();
      if (!originalTeacherIssue) {
        return null;
      }
      return OriginalTeacherIssueLabelMap[originalTeacherIssue];
    },
  }),

  adminCol.accessor('canceledAtInstant', {
    id: SessionPageColumns.canceledAtInstant,
    header: () => <ArrowTooltip trigger="Cancelation...">Cancelation timestamp</ArrowTooltip>,
    cell: ({ getValue }) => {
      const canceledAtInstant = getValue();

      if (!canceledAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(canceledAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),
];

export const pastSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionCompletionStatus,
    header: 'Teacher display - class status',
    cell: ({ getValue }) => {
      const {
        sessionCompletionStatus,
        coachSessionRemarksV2,
        zoom,
        startInstant,
        endInstant,
        addedCoachSessionRemarksV2,
      } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant, addedCoachSessionRemarksV2 }}
        />
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback',
    cell: ({ getValue }) => {
      const session = getValue();
      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown - Unknown</span>;
      return (
        <>
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name} - <span className="text-blue-600">{school.org.name}</span>
          </Link>
        </>
      );
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.recordingLink,
    header: 'Recording',
    cell: ({ getValue }) => {
      const session = getValue();
      const recordingUrls = session.zoom?.recordingUrls ?? [];
      if (recordingUrls.length === 0) {
        return <div>-</div>;
      }

      return (
        <div className="flex flex-col gap-1">
          {recordingUrls.map((url, index) => (
            <LinkToExternal key={`${url}-${index}`} href={url} className="text-blue-600 hover:text-blue-800 underline">
              {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
            </LinkToExternal>
          ))}
        </div>
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ getValue }) => {
      const coach = getValue();
      return coach ? <PeopleBadgeList people={[coach]} /> : null;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.substituteTimestamp,
    header: 'Substitute timestamp',
    cell: ({ getValue }) => {
      const session = getValue();
      const substitutionRequest = session.substitutionRequests[0];
      if (!substitutionRequest?.acceptedAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(substitutionRequest.acceptedAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),

  adminCol.accessor('canceledAtInstant', {
    id: SessionPageColumns.canceledAtInstant,
    header: 'Cancelation timestamp',
    cell: ({ getValue }) => {
      const canceledAtInstant = getValue();

      if (!canceledAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(canceledAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),
];

export const subSessionsPageColumns = (displayTimeZone: string, from?: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor('id', {
    id: SessionPageColumns.sessionId,
    header: '',
    cell: ({ getValue }) => {
      const searchProps = from ? { search: { from } } : {};
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: getValue() }} {...searchProps}>
          <ViewIcon size={16} />
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.subSessionDrawer,
    header: '',
    cell: ({ getValue }) => {
      const session = getValue();
      return (
        <SubSessionDrawer
          studentGroupId={session.studentGroupId}
          session={session}
          trigger={
            <Button variant="ghost" size="icon">
              <UserPenIcon size={18} />
            </Button>
          }
        />
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue }) => {
      const startInstant = getValue();
      return formatDateWithDayOfWeek(startInstant, displayTimeZone);
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral);
    },
  }),

  adminCol.accessor('subNotes', {
    id: 'subNotes',
    header: 'Sub notes',
    maxSize: 250,
    cell: ({ getValue }) => {
      const subNotes = getValue();
      if (!subNotes) return '—';
      return (
        <div className="max-w-[250px] truncate" title={subNotes}>
          {subNotes}
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.coach?.coachProfile?.zoomPersonalMeetingId, {
    id: SessionPageColumns.zoomPersonalMeetingId,
    header: 'Zoom ID',
    cell: ({ getValue }) => getValue() || '—',
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.zoom?.hostUrl, {
    id: SessionPageColumns.zoomHostUrl,
    header: 'Zoom host URL',
    cell: ({ getValue }) => {
      const link = getValue();
      if (!link) return '—';

      return (
        <div className="flex items-center gap-2">
          <SessionLink href={link}>Open Zoom host link</SessionLink>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown</span>;
      return (
        <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
          {school.name}
        </Link>
      );
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grades',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row.students?.length ?? 0, {
    id: SessionPageColumns.numberOfStudents,
    header: '# of students',
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.substituteTimestamp,
    header: 'Substitute timestamp',
    cell: ({ getValue }) => {
      const session = getValue();
      const { mostRecentRequest } = getSessionSubstitutionRequestSummary(session);
      if (!mostRecentRequest?.createdAtInstant) return '-';

      const { formattedTime, formattedDate } = formatInstant(
        mostRecentRequest.createdAtInstant,
        displayTimeZone,
        {
          hour12: true,
        },
        ', ',
      );

      return <span className="lowercase">{`${formattedDate}, ${formattedTime}`}</span>;
    },
  }),
];

export const overlappingSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.org?.name;
      if (!districtName) return <span>Unknown</span>;
      return getDistrictColumn({
        orgName: districtName,
        shouldHaveLink: false,
      });
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown</span>;
      return (
        <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
          {school.name}
        </Link>
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),
];

export const teacherFutureSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-future' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-future' }}>
          {formatTimeRange(startInstant, endInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('students', {
    id: SessionPageColumns.students,
    header: 'Students',
    cell: ({ getValue }) => {
      const students = getValue();
      return <PeopleBadgeList people={students} wrap={true} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: SessionPageColumns.grades,
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: SessionPageColumns.school,
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      return school?.name || 'Unknown';
    },
  }),

  adminCol.accessor('school', {
    id: SessionPageColumns.district,
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      return school?.org?.name || 'Unknown';
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonName,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <CopyableLink
          externalHref={link}
          className="text-blue-600 hover:text-blue-800 underline"
          showExternalHrefIcon={false}
        >
          {lesson.name}
        </CopyableLink>
      );
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),
];

export const teacherPastSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-past' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-past' }}>
          {formatTimeRange(startInstant, endInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionCompletionStatus,
    header: 'Completion status',
    cell: ({ getValue }) => {
      const {
        sessionCompletionStatus,
        coachSessionRemarksV2,
        zoom,
        startInstant,
        endInstant,
        addedCoachSessionRemarksV2,
      } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant, addedCoachSessionRemarksV2 }}
        />
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback status',
    cell: ({ getValue }) => {
      const session = getValue();
      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  adminCol.accessor('students', {
    id: SessionPageColumns.students,
    header: 'Students',
    cell: ({ getValue }) => {
      const students = getValue();
      return <PeopleBadgeList people={students} wrap={true} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: SessionPageColumns.grades,
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: SessionPageColumns.school,
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      return (
        <>
          {school?.name || 'Unknown'} - {school?.org.name || 'Unknown'}
        </>
      );
    },
  }),

  adminCol.accessor('lessonLink', {
    id: 'lessonName',
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!link || !lesson) return '—';

      return (
        <div className="flex items-center gap-2">
          <Link to={'/lessons/$lessonId'} params={{ lessonId: lesson.id }} className="underline">
            {lesson.name}
          </Link>
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.recordingLink,
    header: 'Recording',
    cell: ({ getValue }) => {
      const session = getValue();
      const recordingUrls = session.zoom?.recordingUrls ?? [];
      if (recordingUrls.length === 0) {
        return <div>-</div>;
      }

      return (
        <div className="flex flex-col gap-1">
          {recordingUrls.map((url, index) => (
            <LinkToExternal key={`${url}-${index}`} href={url} className="text-blue-600 hover:text-blue-800 underline">
              {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
            </LinkToExternal>
          ))}
        </div>
      );
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),
];
